// Seattle University: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Seattle University posts no Common Data Set on seattleu.edu (its
//    institutional research team, Decision Support, publishes no public data
//    page), so the first-year counts come from the U.S. Department of
//    Education's IPEDS Admissions survey for Fall 2024, first-time
//    degree-seeking students (College Navigator, unit 236595, with the
//    admitted and enrolled counts from the ADM2024 complete data file).
//  - seattleu.edu first-year admissions: Deadlines (plans, decision timing,
//    Early Decision terms) and How to Apply (test-optional policy). The
//    pages carry no year label; they are the pages as posted for the cycle
//    open now (Fall 2027 entry).
//  - Move to Semesters: the quarter-to-semester change that starts with
//    summer 2027, the term students entering in fall 2027 begin in.
//  - Undergraduate Cost of Attendance: the 2026-27 sample student budget
//    for an undergraduate living on campus, from Student Financial Services.
//  - College Scorecard API, school id 236595 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=236595#admsns',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Seattle University First-Year Admissions, Deadlines',
        url: 'https://www.seattleu.edu/admissions-aid/undergraduate-admissions/first-year-admissions/how-to-apply/deadlines/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Seattle University First-Year Admissions, How to Apply',
        url: 'https://www.seattleu.edu/admissions-aid/undergraduate-admissions/first-year-admissions/how-to-apply/',
        read: '2026-10-01',
    },
    semesters: {
        label: 'Seattle University, Move to Semesters',
        url: 'https://www.seattleu.edu/who-we-are/move-to-semesters/',
        read: '2026-10-01',
    },
    coa: {
        label: 'Seattle University Student Financial Services, Undergraduate Cost of Attendance',
        url: 'https://www.seattleu.edu/admissions-aid/tuition-and-fees/undergraduate-cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?236595-Seattle-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'seattle-university',
    kbId: 'seattle_university',
    name: 'Seattle University',
    shortName: 'Seattle U',
    location: 'Seattle, WA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Seattle University brand identity, color); decoration only.
    branding: {
        primary: '#AA0000',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/seattle_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 8468,
        admitted: 6514,
        enrolled: 888,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Seattle University does not post a Common Data Set, so these counts come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole university and none by college or major.',
        source: 'ipeds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'Early Decision is open only to Nursing and Diagnostic Ultrasound applicants, who may also use Early Action or Regular Decision. Early Action is open to all majors and is not binding. Seattle University does not publish early decision or early action counts.',
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision (Nursing and Diagnostic Ultrasound only)', deadline: 'November 1', decision: 'Early December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Late December', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Early March', binding: false },
        ],
        note: 'You apply through the Common Application. If you send test scores, they must arrive by November 1 for Early Action and January 15 for Regular Decision. Early Action admits get financial aid offers by mid-February and Regular Decision admits by late March; both have until May 1 to enroll. Applications after January 15 are read as space allows. The How to Apply page gives mid-December for both early plans and mid-March for Regular Decision. Applicants to Cornish College of the Arts also send an artist statement and meet audition or portfolio requirements.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if you are accepted, you are expected to enroll at Seattle University. Admitted students get their decision and financial aid package within a few weeks. The Deadlines page gives two dates to enroll by, January 15 in one place and December 15 in another.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Seattle University is test-optional for all first-year applicants: you choose on the Common Application whether your SAT or ACT scores are reviewed. Applicants without scores are not at a disadvantage and are considered for scholarships and aid the same way.',
        source: 'apply',
    },

    // Announced changes already in effect for students entering in fall 2027.
    currentChanges: [
        { topic: 'Academic calendar', line: 'Seattle University moves from quarters to semesters starting with summer 2027. Students who enroll in fall 2027 or later study entirely on the semester calendar; fall semester classes begin August 30, 2027.', source: 'semesters' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition (full time)', amount: 59400 },
            { item: 'Housing (average, on campus)', amount: 11202 },
            { item: 'Food', amount: 6426 },
            { item: 'Personal expenses', amount: 1968 },
            { item: 'Transportation', amount: 1716 },
            { item: 'Books and supplies', amount: 594 },
            { item: 'Technology fee', amount: 642 },
            { item: 'Wellness fee', amount: 516 },
            { item: 'Activity fee', amount: 198 },
            { item: 'Loan fee', amount: 64 },
        ],
        total: 82726,
        note: 'Seattle University\'s 2026-27 sample budget for a full-time undergraduate living on campus, for the fall, winter and spring quarters. The loan fee applies only to students who borrow. Students entering in fall 2027 start on semesters; its Move to Semesters page says the change will not by itself raise the total cost of a program and that 2027-2028 tuition, fees and aid will be adjusted to the semester calendar.',
        source: 'coa',
        netPrice: {
            average: 34324,
            byIncome: [
                { income: '$0 to $30,000', amount: 21379 },
                { income: '$30,001 to $48,000', amount: 21026 },
                { income: '$48,001 to $75,000', amount: 22301 },
                { income: '$75,001 to $110,000', amount: 32633 },
                { income: 'Over $110,000', amount: 45994 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Seattle University\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
